import { useState, useRef, useCallback } from "react";

import { cn } from "@/lib/utils";
import type { SkillsCategoryCount } from "@optidev/shared-types";

const CATEGORY_LABELS: Record<string, string> = {
  "creative-design": "Design",
  "document-processing": "Documents",
  "business-marketing": "Marketing",
  development: "Development",
  "web-development": "Web Dev",
  "enterprise-communication": "Communication",
  "workflow-automation": "Automation",
  security: "Security",
  productivity: "Productivity",
  database: "Database",
  analytics: "Analytics",
  "ai-research": "AI Research",
  media: "Media",
  video: "Video",
  scientific: "Scientific",
};

interface CategoryFilterProps {
  categories: SkillsCategoryCount[];
  selected: string | null;
  onSelect: (category: string | null) => void;
  totalCount: number;
  customCount?: number;
}

export function CategoryFilter({
  categories,
  selected,
  onSelect,
  totalCount,
  customCount = 0,
}: CategoryFilterProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, scrollLeft: 0 });
  const hasDraggedRef = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = scrollRef.current;
    if (!container) return;
    setIsDragging(true);
    hasDraggedRef.current = false;
    dragStartRef.current = { x: e.pageX, scrollLeft: container.scrollLeft };
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const container = scrollRef.current;
      if (!container) return;
      e.preventDefault();
      const dx = e.pageX - dragStartRef.current.x;
      if (Math.abs(dx) > 3) hasDraggedRef.current = true;
      container.scrollLeft = dragStartRef.current.scrollLeft - dx;
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(
    (category: string | null) => {
      // Don't trigger click if user was dragging
      if (hasDraggedRef.current) return;
      onSelect(category);
    },
    [onSelect],
  );

  const pillClass = (isSelected: boolean) =>
    cn(
      "inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium",
      "border transition-colors duration-200 whitespace-nowrap flex-none select-none",
      isSelected
        ? "bg-foreground/90 text-background border-foreground/90"
        : "bg-card text-muted-foreground border-border hover:text-foreground",
    );

  return (
    <div
      ref={scrollRef}
      className={cn(
        "flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none w-full",
        isDragging ? "cursor-grabbing" : "",
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      style={{ scrollbarWidth: "none" }}
    >
      <button
        onClick={() => handleClick(null)}
        className={pillClass(selected === null)}
      >
        All
      </button>
      {customCount > 0 && (
        <button
          onClick={() => handleClick("custom")}
          className={pillClass(selected === "custom")}
        >
          Custom
        </button>
      )}
      {categories.map((cat) => (
        <button
          key={cat.category}
          onClick={() => handleClick(cat.category)}
          className={pillClass(selected === cat.category)}
        >
          {CATEGORY_LABELS[cat.category] || cat.category}
        </button>
      ))}
    </div>
  );
}
